import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTrailerTypes } from "@/hooks/useTrailerTypes";
import { useUserRole } from "@/hooks/useUserRole";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const KIND_OPTIONS = [
  { value: "dry_van", label: "Dry Van" },
  { value: "reefer", label: "Reefer" },
  { value: "roughneck", label: "Roughneck" },
  { value: "maxxim_hopper", label: "Maxxim Hopper" },
  { value: "container", label: "Container" },
  { value: "bobtail", label: "Bobtail" },
] as const;

const KIND_LABEL_MAP: Record<string, string> = Object.fromEntries(
  KIND_OPTIONS.map((k) => [k.value, k.label])
);

function buildLabel(kind: string, length: number): string {
  if (kind === "bobtail" && length === 0) return "Bobtail";
  return `${length}' ${KIND_LABEL_MAP[kind] ?? kind}`;
}

function buildSlug(kind: string, length: number): string {
  if (kind === "bobtail" && length === 0) return "bobtail";
  return `${length}_${kind}`;
}

export function TrailerTypesTab() {
  const { data: trailerTypes, isLoading } = useTrailerTypes();
  const { roles } = useUserRole();
  const queryClient = useQueryClient();

  const canManage = roles.includes("admin") || roles.includes("dispatcher");

  const [kind, setKind] = useState("");
  const [lengthFt, setLengthFt] = useState("");
  const [lengthError, setLengthError] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: async ({ kind, length_ft }: { kind: string; length_ft: number }) => {
      const label = buildLabel(kind, length_ft);
      const slug = buildSlug(kind, length_ft);
      const { error } = await supabase
        .from("trailer_types")
        .insert({ kind, length_ft, label, slug });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trailer-types"] });
      toast.success("Trailer type added");
      setKind("");
      setLengthFt("");
      setLengthError(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("trailer_types").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trailer-types"] });
      toast.success("Trailer type deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSubmit = () => {
    if (!kind) {
      toast.error("Please select a kind");
      return;
    }
    const num = parseInt(lengthFt, 10);
    if (isNaN(num) || num < 0 || num > 100) {
      setLengthError("Must be 0–100");
      return;
    }
    setLengthError(null);
    addMutation.mutate({ kind, length_ft: num });
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kind</TableHead>
              <TableHead>Length (ft)</TableHead>
              <TableHead>Label</TableHead>
              {canManage && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={canManage ? 4 : 3} className="text-center py-8">Loading...</TableCell>
              </TableRow>
            ) : trailerTypes?.length ? (
              trailerTypes.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{KIND_LABEL_MAP[t.kind] ?? t.kind}</TableCell>
                  <TableCell>{t.length_ft}</TableCell>
                  <TableCell>{t.label}</TableCell>
                  {canManage && (
                    <TableCell>
                      <ConfirmDeleteButton
                        onConfirm={() => deleteMutation.mutate(t.id)}
                        entityName="trailer type"
                        entityLabel={t.label}
                        isPending={deleteMutation.isPending}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={canManage ? 4 : 3} className="text-center py-8 text-muted-foreground">No trailer types found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {canManage && (
        <div className="flex items-end gap-3">
          <div className="w-48">
            <label className="text-sm font-medium mb-1 block">Kind</label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger>
                <SelectValue placeholder="Select kind" />
              </SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32">
            <label className="text-sm font-medium mb-1 block">Length (ft)</label>
            <Input
              type="number"
              min={0}
              max={100}
              value={lengthFt}
              onChange={(e) => {
                setLengthFt(e.target.value);
                setLengthError(null);
              }}
              placeholder="e.g. 53"
              className={lengthError ? "border-destructive" : ""}
            />
            {lengthError && <p className="text-xs text-destructive mt-1">{lengthError}</p>}
          </div>
          <Button onClick={handleSubmit} disabled={addMutation.isPending}>
            <Plus className="h-4 w-4 mr-1" />
            Add Type
          </Button>
        </div>
      )}
    </div>
  );
}
